import { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Check, Loader2, Trash2, CircleCheck, CircleX, AlertTriangle, Plug, Zap, Send, BookOpen, Archive, Copy, ExternalLink, Link2 } from 'lucide-react';
import FactsLibrary from './FactsLibrary';
import AdAudiences from './AdAudiences';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

function ApiStatusCard({ api, onTestConnection, testingService, testResult }) {
  const canTest = (api.service === 'mailerlite' || api.service === 'buffer') && api.connected;
  const isTesting = testingService === api.service;
  const result = testResult?.[api.service];

  return (
    <div data-testid={`api-status-${api.service}`} className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-sans text-hg-text font-medium">{api.name}</div>
          <div className="text-xs text-hg-text-dim font-sans mt-0.5">{api.description}</div>
        </div>
        <div className="flex items-center gap-2">
          {api.archive ? (
            <span className="flex items-center gap-1 text-xs font-sans text-hg-text-dim border border-hg-border rounded-sm px-2 py-0.5" title="Kept for history — the Hub no longer sends through this service">
              <Archive size={12} strokeWidth={1.5} /> Archive (read-only) · 795 campaigns
            </span>
          ) : api.connected ? (
            <span className="flex items-center gap-1 text-xs font-sans text-hg-green">
              <CircleCheck size={14} strokeWidth={1.5} /> Connected
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs font-sans text-hg-text-dim">
              <CircleX size={14} strokeWidth={1.5} /> Not Connected
            </span>
          )}
        </div>
      </div>
      {canTest && (
        <div className="flex items-center gap-3 pt-2 border-t border-hg-border">
          <button
            data-testid={`test-connection-${api.service}`}
            onClick={() => onTestConnection(api.service)}
            disabled={isTesting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green transition-colors disabled:opacity-40"
          >
            {isTesting ? <Loader2 size={12} className="animate-spin" /> : <Plug size={12} strokeWidth={1.5} />}
            {isTesting ? 'Testing...' : 'Test Connection'}
          </button>
          {result && (
            <span className={`text-xs font-sans ${result.success ? 'text-hg-green' : 'text-red-400'}`}>
              {result.message}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// The keyed marketing-pack URLs (Australia, USA, Facility Partner Packet). THE KEY IS THE LINK — these are the whole credential,
// so they are fetched from an auth-gated endpoint rather than built here: nothing puts a
// pack key in the JavaScript bundle or in the repo. A pack with no key configured says so
// and offers nothing to copy, because a link with an empty `k` looks like it works and 404s
// for whoever it was sent to.
function MarketingPackLinks({ packs }) {
  const [copied, setCopied] = useState('');

  const copy = async (pack) => {
    try {
      await navigator.clipboard.writeText(pack.url);
      setCopied(pack.key);
      setTimeout(() => setCopied(''), 2000);
    } catch (e) { console.error(e); }
  };

  if (!packs) return null;

  return (
    <div className="space-y-3">
      <h2 className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Marketing pack links</h2>
      <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-3">
        <p className="text-xs text-hg-text-dim font-sans leading-relaxed">
          One link per pack, holding every film, photo, logo, badge and document in it. The Facility
          Partner Packet is also the link Email 0 sends a partner lead. Each facility partner's
          co-branded launch kit has its own link here too, and nothing else shows those files.
          <span className="text-hg-text"> The key is the link</span> — anyone holding the whole URL can
          open it, so send it only to the people the pack is for. Nothing public links to any of them.
        </p>
        {packs.map((pack) => (
          <div key={pack.key} data-testid={`pack-link-${pack.key}`} className="py-2 border-b border-hg-border last:border-b-0 space-y-2">
            <div className="flex items-center gap-2">
              <Link2 size={14} className="text-hg-green flex-shrink-0" strokeWidth={1.5} />
              <span className="text-sm font-sans text-hg-text">{pack.title}</span>
              <span className="text-[10px] font-mono text-hg-text-dim">{pack.collection}</span>
            </div>
            {pack.configured ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-[11px] font-mono text-hg-text/80">
                  {pack.url}
                </code>
                <button
                  data-testid={`copy-pack-link-${pack.key}`}
                  onClick={() => copy(pack)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green transition-colors"
                >
                  {copied === pack.key ? <Check size={12} className="text-hg-green" /> : <Copy size={12} strokeWidth={1.5} />}
                  {copied === pack.key ? 'Copied' : 'Copy'}
                </button>
                <a
                  href={pack.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text-dim hover:border-hg-green hover:text-hg-green transition-colors"
                >
                  <ExternalLink size={12} strokeWidth={1.5} /> Open
                </a>
              </div>
            ) : (
              <div className="text-[11px] font-sans text-yellow-500 flex items-center gap-1.5">
                <AlertTriangle size={12} /> No key configured — set <code className="font-mono">{pack.env_var}</code> in the Railway environment.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Settings -> Instagram bio -> "Featured film" (Randall, 22 Sep 2026): the film behind "Watch the
// latest film" on hub.henry-griffitts.com/ig. Pinned to the Ol' Trusty launch film until changed;
// "Newest film" clears the pin. Only approved films the bio can show are offered.
function InstagramBioFilm() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/settings/ig-bio-film`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load the featured film.'));
  }, []);

  const choose = async (filmId) => {
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/settings/ig-bio-film`, { film_id: filmId });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save the featured film.');
    } finally { setSaving(false); }
  };

  const pin = data ? (data.pin === null ? data.default : data.pin) : '';

  return (
    <div className="space-y-3">
      <h2 className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Instagram bio</h2>
      <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-3">
        <p className="text-xs text-hg-text-dim font-sans leading-relaxed">
          <span className="text-hg-text">Featured film</span> — the film behind “Watch the latest film” on{' '}
          <a href="https://hub.henry-griffitts.com/ig" target="_blank" rel="noopener noreferrer" className="text-hg-green hover:underline">hub.henry-griffitts.com/ig</a>.
          Phones get the vertical cut where one exists. “Newest film” picks the most recent upload instead.
        </p>
        {data?.error && (
          <div className="text-[11px] font-sans text-yellow-500 flex items-center gap-1.5"><AlertTriangle size={12} /> {data.error}</div>
        )}
        {data?.choices && (
          <select
            data-testid="ig-bio-featured-film"
            value={pin}
            disabled={saving}
            onChange={(e) => choose(e.target.value)}
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:border-hg-green/50 disabled:opacity-50"
          >
            <option value="">Newest film (by upload date)</option>
            {data.choices.map((c) => (
              <option key={c.id} value={c.id}>{c.title}{c.vertical ? ' · vertical' : ''}</option>
            ))}
          </select>
        )}
        {data?.showing && (
          <div className="text-[11px] font-sans text-hg-text-dim flex items-center gap-1.5">
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} className="text-hg-green" />}
            Showing now: <span className="text-hg-text">{data.showing.title}</span>
          </div>
        )}
        {error && <div className="text-[11px] font-sans text-red-400">{error}</div>}
      </div>
    </div>
  );
}

function VoiceEditor({ settingKey, label, value, onSave, saving }) {
  const [text, setText] = useState(value || '');

  useEffect(() => {
    setText(value || '');
  }, [value]);

  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-sans text-hg-text font-medium">{label}</label>
        <button
          data-testid={`save-prompt-${settingKey}`}
          onClick={() => onSave(settingKey, text)}
          disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
      </div>
      <textarea
        data-testid={`prompt-editor-${settingKey}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full bg-hg-bg border border-hg-border rounded-sm p-3 text-xs text-hg-text/80 font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-hg-green resize-none"
        rows={10}
      />
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState('');
  const [clearing, setClearing] = useState('');
  const [testingService, setTestingService] = useState('');
  const [testResult, setTestResult] = useState({});
  const [testSending, setTestSending] = useState('');
  const [testSendResult, setTestSendResult] = useState(null);
  const [testRecipient, setTestRecipient] = useState('randallhenryhg@gmail.com');
  const [packLinks, setPackLinks] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, statusRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/settings/api-status`),
        ]);
        const settingsMap = {};
        settingsRes.data.settings.forEach(s => { settingsMap[s.setting_key] = s.setting_value; });
        setSettings(settingsMap);
        setApis(statusRes.data.apis);
      } catch (e) { console.error(e); }
      // Its own request and its own catch: a pack-links failure must cost the card, never
      // the rest of Settings. Left as null so the card renders nothing rather than an
      // empty shell claiming there are no packs.
      try {
        const res = await axios.get(`${API}/settings/marketing-pack-links`);
        setPackLinks(res.data.packs);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const showNotif = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(''), 3000);
  };

  const handleTestConnection = async (service) => {
    setTestingService(service);
    setTestResult(prev => ({ ...prev, [service]: null }));
    try {
      const res = await axios.post(`${API}/settings/test-${service}`);
      setTestResult(prev => ({ ...prev, [service]: { success: true, message: res.data.message } }));
      showNotif(res.data.message);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Connection test failed';
      setTestResult(prev => ({ ...prev, [service]: { success: false, message: detail } }));
    }
    setTestingService('');
  };

  const handleTestSend = async (style) => {
    setTestSending(style);
    setTestSendResult(null);
    try {
      const res = await axios.post(`${API}/settings/test-send`, {
        style,
        recipient_email: testRecipient || 'randallhenryhg@gmail.com',
      });
      const { delivery, message, mailerlite_url } = res.data || {};
      if (delivery === 'manual' && mailerlite_url) {
        window.open(mailerlite_url, '_blank', 'noopener');
        setTestSendResult({
          success: true,
          manual: true,
          message: `Draft created in Mailerlite — opened in a new tab. Click the green Send button there to deliver to ${testRecipient}.`,
          url: mailerlite_url,
        });
      } else {
        setTestSendResult({ success: true, manual: false, message: message || 'Sent.' });
        showNotif(message || 'Test sent');
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Test send failed';
      setTestSendResult({ success: false, message: detail });
    }
    setTestSending('');
  };

  const handleSavePrompt = async (key, value) => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings`, { setting_key: key, setting_value: value });
      setSettings({ ...settings, [key]: value });
      showNotif('Voice prompt saved');
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleClearHistory = async (module) => {
    if (!window.confirm(`Are you sure you want to clear all ${module} history? This cannot be undone.`)) return;
    setClearing(module);
    try {
      await axios.delete(`${API}/settings/clear-history/${module}`);
      showNotif(`${module.charAt(0).toUpperCase() + module.slice(1)} history cleared`);
    } catch (e) { console.error(e); }
    setClearing('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-hg-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notification && (
        <div className="fixed top-4 right-4 z-50 bg-hg-surface border border-hg-green/30 rounded-sm px-4 py-3 text-sm text-hg-green font-sans flex items-center gap-2 animate-fade-in">
          <Check size={14} /> {notification}
        </div>
      )}

      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">SETTINGS</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">API connections, AI voice prompts, and data management.</p>
      </div>

      <div className="space-y-3">
        <h2 className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">API Connections</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {apis.map((api, i) => <ApiStatusCard key={i} api={api} onTestConnection={handleTestConnection} testingService={testingService} testResult={testResult} />)}
        </div>
      </div>

      <AdAudiences />

      <MarketingPackLinks packs={packLinks} />

      <InstagramBioFilm />

      <div className="space-y-3">
        <h2 className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Send Sample to Inbox</h2>
        <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-3">
          <p className="text-xs text-hg-text-dim font-sans leading-relaxed">
            Sends a real test render of each HG template <span className="text-hg-text">instantly via MailerLite</span> to
            the recipient below (the default HG test inboxes are always copied). If MailerLite ever rejects the
            instant send, you'll get a one-click link to finish it from the MailerLite dashboard instead.
          </p>
          <div className="space-y-1">
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Recipient Email</label>
            <input
              data-testid="test-send-recipient"
              type="email"
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              placeholder="randallhenryhg@gmail.com"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:border-hg-green/50"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { id: 'newsletter', label: 'Test Newsletter' },
              { id: 'promotional', label: 'Test Promotional' },
              { id: 'announcement', label: 'Test Announcement' },
            ].map((opt) => (
              <button
                key={opt.id}
                data-testid={`test-send-${opt.id}`}
                onClick={() => handleTestSend(opt.id)}
                disabled={testSending === opt.id || !testRecipient}
                className="flex items-center justify-center gap-2 px-3 py-2 text-xs font-sans border border-hg-green/30 bg-hg-green/5 text-hg-green rounded-sm hover:bg-hg-green/10 transition-colors disabled:opacity-40"
              >
                {testSending === opt.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} strokeWidth={1.5} />}
                {testSending === opt.id ? 'Sending...' : opt.label}
              </button>
            ))}
          </div>
          {testSendResult && (
            <div className={`text-[11px] font-sans p-2 rounded-sm ${testSendResult.success ? 'bg-hg-green/10 text-hg-green' : 'bg-red-500/10 text-red-400'}`}>
              {testSendResult.message}
              {testSendResult.manual && testSendResult.url && (
                <a
                  href={testSendResult.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="test-send-mailerlite-link"
                  className="block mt-1 underline hover:no-underline"
                >
                  Open in Mailerlite →
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Brand Voice</h2>
        <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-3">
          <div className="flex items-start gap-3">
            <BookOpen size={16} className="text-hg-green mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <div className="space-y-1">
              <p className="text-sm font-sans text-hg-text">HG Brand Voice & Identity Guide</p>
              <p className="text-xs text-hg-text-dim font-sans leading-relaxed">
                The full brand voice guide is the system prompt for every AI generation across Social, Newsletter, and Email Studios. The lint engine catches banned words ("revolutionary", "world-class", "click here", etc.), corporate third-person, and exclamation points outside real quotes — showing them in red on every draft so you can fix before publishing.
              </p>
            </div>
          </div>
          <div className="text-[11px] text-hg-text-dim font-sans flex items-center gap-1.5 pt-2 border-t border-hg-border">
            <Check size={11} className="text-hg-green" /> Voice engine active · Facts Library connected · Watermark on AI images
          </div>
        </div>
      </div>

      <FactsLibrary />

      <div className="space-y-3">
        <h2 className="text-xs font-sans text-hg-text-dim uppercase tracking-wider">Data Management</h2>
        <div className="bg-hg-surface border border-hg-border rounded-sm p-5 space-y-3">
          <div className="flex items-center gap-2 text-xs text-yellow-500 font-sans mb-2">
            <AlertTriangle size={14} /> These actions are permanent and cannot be undone.
          </div>
          {['social', 'newsletter', 'email'].map((module) => (
            <div key={module} className="flex items-center justify-between py-2 border-b border-hg-border last:border-b-0">
              <span className="text-sm font-sans text-hg-text capitalize">{module} History</span>
              <button
                data-testid={`clear-${module}-history`}
                onClick={() => handleClearHistory(module)}
                disabled={!!clearing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-red-500/30 text-red-400 rounded-sm hover:bg-red-500/10 transition-colors disabled:opacity-40"
              >
                {clearing === module ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Clear
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
